import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Modal } from "react-bootstrap";
import { CredentialInformation, IActionHandler, WebAuthnKeyRegister, WebAuthnKeyAuthenticate, IFetchResponse, KSSAuthParams } from "@preveil-api";
import {
  Account, Helpers, KeyStorageAPI, KeyStorageData, useAppSelector, GlobalErrorMessages, Message, MessageHandlerDisplayType,
  MessageAnchors, useAppDispatch, ChangeAuthenticationSteps, ChangeAuthenticationModalSteps, KeystorageResponseErrorCodes, StatusCode, SettingsSuccessMessages, AccountErrorMessages, SettingsErrorMessages, AuthenticationTypes, PublicRoutes,
  keystorageApi, LocalAccountStorage, authenticateWebAuthnKey, registerWebAuthnKey, MessageToastTypes, 
  getWebAuthErrorMessage,
  AuthenticationConstants,
  encryptPhoneNumber} from "src/common";
import { Loading, PageHeader, SharedSmsCodeVerificationForm, PhoneNumberForm, SharedCurrentPasswordForm } from "src/components";
import { AuthenticatorOptions, CurrentTOTPCodeVerificationForm, TOTPForm, WebAuthKeyAdded } from ".";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import { authenticator } from "otplib";
import security_img from "src/assets/images/security-key.svg";

/* Description: Component in settings to allow an express user to change their authentication method
  between sms, totp and webauthkey (passkey or security key) */
function ChangeAuthenticationComponent() {
  const navigate = useNavigate();
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const session_pv_account = sessionStorage.getItem(AuthenticationConstants.USER_ID_SESSION_KEY);
  const auth_token_store = useAppSelector((state: RootState) => state.account.auth_token);
  const [expires, setExpires] = useState<string>();
  const [auth_type, setAuthType] = useState<AuthenticationTypes>();
  const [auth_token, setAuthToken] = useState<string>();
  const [modal_show, setModalShow] = useState<boolean>(false);
  const [step, setStep] = useState<number>(ChangeAuthenticationSteps.LOADING);
  const [modal_step, setModalStep] = useState<number>(ChangeAuthenticationModalSteps.LOADING);
  const [getAuthMethod] = keystorageApi.endpoints.getAuthenticationMethod.useLazyQuery();
  const dispatch = useAppDispatch();

  const kss_auth_params = useRef<KSSAuthParams>({});
  const new_web_auth_credential_ref = useRef<WebAuthnKeyRegister>();
  const totp_secret_ref = useRef<string>();
  const web_auth_key_type_ref = useRef<string>(!!session_pv_account ? JSON.parse(session_pv_account).two_factor_type : AuthenticationTypes.WEBAUTHN_CREDENTIAL);
  const credential_info_ref = useRef<CredentialInformation>();
  const new_auth_type_ref = useRef<AuthenticationTypes>();
  const phone_number_ref = useRef<string>();
  const edit_key_ref = useRef<boolean>(false);
  const sms_verification_title_ref = useRef<string>("");

  // Description: Triggered on load to check if the auth token is stored in the store
  // if not - set the step to submit password to direct the user to re-enter their password
  useEffect(() => {
    if (!!auth_token_store) {
      setAuthToken(auth_token_store);
      getAuthenticationMethod(auth_token_store);
    } else {
      setStep(ChangeAuthenticationSteps.SUBMIT_PASSWORD);
    }
    return () => {
     reset(); // Reset state on exiting the component
    };
  }, []);

  // Description: Function that calls keystorage server to get the current authentication method
  async function getAuthenticationMethod(auth_token: string) {
    await getAuthMethod({ user_id: current_account?.user_id || "", auth_token: auth_token || "" })
      .unwrap()
      .then((response) => {
        setAuthType(response.authentication_method as AuthenticationTypes);
        setStep(ChangeAuthenticationSteps.CURRENT_AUTHENTICATION);
      })
      .catch((error) => {
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.bad_password), error));
        setStep(ChangeAuthenticationSteps.SUBMIT_PASSWORD);
      });
  }

  // Description: This is specifically for the flow webauth -> webauth. When the current key is a security key - it needs to be removed prior to creating
  // a new key or else the browser will not give both options - PASSKEY and SECURITY KEY.
  function removeSecurityKey(newchallenge?: string) {
    const confirmation_dialog = new Message(
      `<p>Is a security key inserted in your device?<img src=${security_img}></p><p>If so, please <b>remove</b> it before creating a new key.</p>`,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      "Remove Security Key",
      {
        label: "Done",
        data: true,
        action: () => { createNewAuthKey(newchallenge); }
      },
      {
        label: "Cancel",
        action: () => { reset(); }
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Display to the user that they need to verify their new key.
  function VerifyNewKey(web_auth_key: CredentialInformation) {
    const confirmation_dialog = new Message(
      "Continue to finalize your FedRAMP compliant authentication.",
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      "Verify Your New Authentication Method",
      {
        label: "Continue",
        data: true,
        action: () => { getAuthKey(web_auth_key); }
      },
      {
        label: "Cancel",
        action: () => { reset(); }
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Function that calls keystorage server to change the authentication method
  function toggleAuthentication() {
    if (!!auth_token && !!current_account) {
      const account_ids = Account.getAccountIdentifiers(current_account);
      KeyStorageAPI.changeAuthenticationMethod(account_ids, kss_auth_params.current, auth_token, phone_number_ref.current, totp_secret_ref.current, new_web_auth_credential_ref.current)
        .then(async (response) => {
          const message_code = response.data.error || response.data.bad_parameter || "";
          if (!response.isError) {
            const _type = new_auth_type_ref.current === AuthenticationTypes.WEBAUTHN_CREDENTIAL ? web_auth_key_type_ref.current : new_auth_type_ref.current;
            const encrypted_contact = !!phone_number_ref.current ? await encryptPhoneNumber(phone_number_ref.current, current_account.user_key) : undefined;
            LocalAccountStorage.updatePVAccountSession(encrypted_contact, _type as AuthenticationTypes);
            dispatch(uiActions.handleSetMessage(new Message(SettingsSuccessMessages.successfully_changed_authentication_methods)));
            if (new_auth_type_ref.current === AuthenticationTypes.WEBAUTHN_CREDENTIAL) {
              setModalShow(true);
              setModalStep(ChangeAuthenticationModalSteps.WEB_AUTHN_KEY_ADDED);
              setAuthType(new_auth_type_ref.current);
            } else {
              web_auth_key_type_ref.current = AuthenticationTypes.WEBAUTHN_CREDENTIAL;
              setStep(ChangeAuthenticationSteps.LOADING);
              setModalShow(false);
              setAuthType(new_auth_type_ref.current);
              ChangeAuthenticationRequests.handleResetForms(); // add this to reset
            }
          } else {
            handleKSSErrors(message_code, response);
          }
        }).catch((error) => {
          setStep(2);
          ChangeAuthenticationRequests.handleResetForms();
          dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_changing_authentication), error));
        });
    }
  }

  function handleKSSErrors(message_code: any, response: IFetchResponse<any>) {
    const two_factor_codes = [KeystorageResponseErrorCodes.no_sms_auth_header, KeystorageResponseErrorCodes.no_totp_auth_header, KeystorageResponseErrorCodes.no_webauthn_header];
    if (response.status === StatusCode.UNAUTHORIZED_ERROR_CODE && !!message_code && two_factor_codes.includes(message_code)) {
      handleUnauthorizedError(message_code, response);
    } else if (message_code === KeystorageResponseErrorCodes.missing_new_2fa_type) {
      handleMissing2FAError();
    } else if (message_code === KeystorageResponseErrorCodes.bad_totp_code) {
      dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.bad_totp_code), message_code));
      auth_type !== AuthenticationTypes.SMS &&
        setModalStep(ChangeAuthenticationModalSteps.SUBMIT_TOTP_CURRENT_CODE);
    } else if (message_code === KeystorageResponseErrorCodes.bad_sms_code) {
      dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.bad_sms_code), message_code));
      reset();
      auth_type === AuthenticationTypes.SMS ? toggleAuthentication() : setModalStep(ChangeAuthenticationModalSteps.SUBMIT_PHONE_NUMBER);
    } else if (message_code === KeystorageResponseErrorCodes.locked_out) {
      ChangeAuthenticationRequests.handleResetForms();
      navigate(`/${PublicRoutes.logout_route}`);
    } else {
      setStep(2);
      ChangeAuthenticationRequests.handleResetForms();
      dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_changing_authentication), message_code));
    }
  }

  // Description: Handle unauthorized error codes
  function handleUnauthorizedError(message_code: string, response: IFetchResponse<any>) {
    if (message_code === KeystorageResponseErrorCodes.no_sms_auth_header) {
      setModalShow(true);
      sms_verification_title_ref.current = (auth_type === AuthenticationTypes.SMS && !kss_auth_params.current.sms_code) ? "Verify Your Current Authentication Method" : "Verify New Phone Number";
      setModalStep(ChangeAuthenticationModalSteps.SUBMIT_SMS_CODE);
    } else if (message_code === KeystorageResponseErrorCodes.no_totp_auth_header) {
      setModalShow(true);
      (auth_type === AuthenticationTypes.TOTP && !totp_secret_ref.current) ? setModalStep(ChangeAuthenticationModalSteps.SUBMIT_TOTP_CURRENT_CODE) : setModalStep(ChangeAuthenticationModalSteps.SUBMIT_TOTP_CODE);
    } else if (message_code === KeystorageResponseErrorCodes.no_webauthn_header) {
      const verify = (auth_type === AuthenticationTypes.WEBAUTHN_CREDENTIAL && !kss_auth_params.current.web_auth_key) || (!!new_web_auth_credential_ref.current && !kss_auth_params.current.new_web_auth_key);
      if (verify) {
        const authenticate_headers = response.headers.get("Www-Authenticate") as string; // get challenge and credential ID from the backend
        const parsed_data = Object.fromEntries(
          authenticate_headers.replace(/^webauthn\s*/, "") // Remove "webauthn" prefix
            .split(";")
            .map(part => part.trim().split(/=(.+)/)) // Split at the first '=' only
        );
        credential_info_ref.current = parsed_data as CredentialInformation;
        setModalShow(false);
        new_auth_type_ref.current === AuthenticationTypes.WEBAUTHN_CREDENTIAL && auth_type !== AuthenticationTypes.WEBAUTHN_CREDENTIAL ? VerifyNewKey(parsed_data) : getAuthKey(parsed_data);
      } else {
        createNewAuthKey();
      }
    }
  }

  // Description: Handle missing 2FA error code
  function handleMissing2FAError() {
    if (new_auth_type_ref.current === AuthenticationTypes.SMS) {
      setModalShow(true);
      setModalStep(ChangeAuthenticationModalSteps.SUBMIT_PHONE_NUMBER);
    } else if (new_auth_type_ref.current === AuthenticationTypes.TOTP) {
      setModalShow(true);
      totp_secret_ref.current = authenticator.generateSecret(20);
      setModalStep(ChangeAuthenticationModalSteps.SUBMIT_TOTP_CODE);
    } else {
      createNewAuthKey();
    }
  }

  // Description: 
  // https://webauthn.guide/#registration
  async function getAuthKey(web_auth_key: CredentialInformation) {
    setModalShow(false);
    const decoded_context = JSON.parse(atob(web_auth_key.context));
    const use_new_challenge = new_auth_type_ref.current === AuthenticationTypes.WEBAUTHN_CREDENTIAL && auth_type !== AuthenticationTypes.WEBAUTHN_CREDENTIAL;
    authenticateWebAuthnKey(web_auth_key, use_new_challenge).then((params: WebAuthnKeyAuthenticate | undefined) => { 
      if (auth_type === AuthenticationTypes.WEBAUTHN_CREDENTIAL && !kss_auth_params.current.web_auth_key) {
        kss_auth_params.current.web_auth_key = params;
        if (new_auth_type_ref.current === AuthenticationTypes.TOTP) {
          setModalShow(true);
          totp_secret_ref.current = authenticator.generateSecret(20);
          setModalStep(ChangeAuthenticationModalSteps.SUBMIT_TOTP_CODE);
        } else if (new_auth_type_ref.current === AuthenticationTypes.WEBAUTHN_CREDENTIAL) {
          decoded_context.type !== AuthenticationTypes.PASSKEY ? removeSecurityKey(web_auth_key.newchallenge) : createNewAuthKey(web_auth_key.newchallenge);
        } else {
          toggleAuthentication();
        }
      } else {
        web_auth_key_type_ref.current = decoded_context.type;
        kss_auth_params.current.new_web_auth_key = params;
        if (auth_type === AuthenticationTypes.TOTP && !kss_auth_params.current.totp_code) {
          setModalShow(true);
          setModalStep(ChangeAuthenticationModalSteps.SUBMIT_TOTP_CURRENT_CODE);
        } else {
          toggleAuthentication();
        }
      }
    }).catch((error: any) => {  
      ChangeAuthenticationRequests.handleResetForms();
      // "NotAllowedError" is thrown when the user cancels the request or if the request is timed out - we don't know which one so displaying a warning.
      if (error.name === "NotAllowedError" || error.name === "AbortError" || error.name === "InvalidStateError") {
        dispatch(uiActions.handleSetMessage(new Message(getWebAuthErrorMessage(error), MessageHandlerDisplayType.toaster, MessageToastTypes.warning)));
      } else {
        dispatch(uiActions.handleRequestErrors(new Message(getWebAuthErrorMessage(error)), error));
      }
    });
  }

  // Description: Register a new webauthn key
  // https://webauthn.guide/#registration
  function createNewAuthKey(newchallenge?: string) {
    setModalShow(false);
    if (!!current_account) {
      registerWebAuthnKey(current_account.user_id, current_account.display_name).then((register_info: WebAuthnKeyRegister | undefined) => {
        if (!!register_info) {
          new_web_auth_credential_ref.current = register_info;
          !!newchallenge ? VerifyNewKey({challenge: newchallenge, context: register_info.context, newchallenge }) : toggleAuthentication();
        } else {
          ChangeAuthenticationRequests.handleResetForms();
          dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_changing_authentication)));
        }
      }).catch((error: any) => {  
        ChangeAuthenticationRequests.handleResetForms();
        // "NotAllowedError" is thrown when the user cancels the request or if the request is timed out - we don't know which one so displaying a warning.
        if (error.name === "NotAllowedError" || error.name === "AbortError" || error.name === "InvalidStateError") {
          dispatch(uiActions.handleSetMessage(new Message(getWebAuthErrorMessage(error), MessageHandlerDisplayType.toaster, MessageToastTypes.warning)));
        } else {
          dispatch(uiActions.handleRequestErrors(new Message(getWebAuthErrorMessage(error)), error));
        }
      });
    }
  }

  // Description: Functions in handleAction for child components to use.
  const ChangeAuthenticationRequests = {
    // Description: Sets the auth token after directing the user to enter their password (if the auth token is not stored as a session cookie)
    handleSubmitPassword: async (password: string) => {
      if (!!current_account) {
        const _auth_token = Helpers.b64Encode(await KeyStorageData.authenticationToken(current_account.user_id, password));
        setAuthToken(_auth_token);
        getAuthenticationMethod(_auth_token);
      }
    },
    // Description: Submit the phone number and call toggleAuthentication which should trigger an sms challenge
    handleSubmitPhone: (params: { expires: string; phone_number?: string; }) => {
      const _phone_number = params.phone_number || phone_number_ref.current;
      setExpires(params.expires);
      phone_number_ref.current = _phone_number;
      !!_phone_number && toggleAuthentication();
    },
    // Description: handles submitting the sms verification code. 
    handleSubmitSMSVerificationCode: (sms_code: string) => {   
      if (auth_type === AuthenticationTypes.SMS && !kss_auth_params.current.sms_code) {
        kss_auth_params.current.sms_code = sms_code;
      } else {
        kss_auth_params.current.new_sms = sms_code;
      }
      toggleAuthentication();
    },
    // Description: handles submitting the totp verification code.
    handleSubmitTOTPVerificationCode: (totp_code: string) => {
      if (auth_type === AuthenticationTypes.TOTP && !kss_auth_params.current.totp_code) {
        kss_auth_params.current.totp_code = totp_code;
      } else {
        kss_auth_params.current.new_totp = totp_code;
      }
      toggleAuthentication();
    },
    // Description: Reset all form states and go back to first step
    handleResetForms: () => {
      reset();
      setStep(ChangeAuthenticationSteps.CURRENT_AUTHENTICATION);
      setModalStep(ChangeAuthenticationModalSteps.LOADING);
      setModalShow(false);
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    },
  };

  // Description: Reset all refs and states
  function reset() {
    setExpires(undefined);
    kss_auth_params.current = {};
    totp_secret_ref.current = undefined;
    new_web_auth_credential_ref.current = undefined;
    phone_number_ref.current = undefined;
    sms_verification_title_ref.current = "";
    setModalShow(false);
    !!auth_token && getAuthenticationMethod(auth_token);
  }

  //  Description: Handle all actions from Children forms
  function handlePageActions(actionObj: IActionHandler) {
    const callback = `handle${actionObj.actionType}`;
    // Handle local calls:
    if ((ChangeAuthenticationRequests as any)[callback] instanceof Function) {
      (ChangeAuthenticationRequests as any)[callback](actionObj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, actionObj.actionType);
      ChangeAuthenticationRequests.handlePageErrorMessage({ message, stack: actionObj });
    }
  }

  // Description: Handle the change of two-factor type - SMS, TOTP, or WebAuthn
  function handleTwoFactorTypeChange(type: AuthenticationTypes, edit?: boolean) {
    new_auth_type_ref.current = type;
    edit && (edit_key_ref.current = true); // setting this to detect if the user is switching from webauthn to webauthn
    setModalShow(true);
    setModalStep(ChangeAuthenticationModalSteps.LOADING);
    if (type === AuthenticationTypes.SMS) {
      setModalStep(ChangeAuthenticationModalSteps.SUBMIT_PHONE_NUMBER);
    } else if (type === AuthenticationTypes.WEBAUTHN_CREDENTIAL && auth_type !== AuthenticationTypes.WEBAUTHN_CREDENTIAL) {
      createNewAuthKey();
    } else {
      toggleAuthentication();
    }
  }

  // Description: Render the correct form
  function ChangeAuthenticationForms() {
    switch (step) {
      case ChangeAuthenticationSteps.SUBMIT_PASSWORD:
        return <SharedCurrentPasswordForm submit={ChangeAuthenticationRequests.handleSubmitPassword} />;
      case ChangeAuthenticationSteps.CURRENT_AUTHENTICATION:
        return <>
          <h4 className="border-bottom">Choose one type of authentication for your login security protection.</h4>
          {!!auth_type && <AuthenticatorOptions auth_type={auth_type} web_auth_key_type={web_auth_key_type_ref.current} handleTwoFactorTypeChange={handleTwoFactorTypeChange} />}
        </>;
      default:
        return <Loading message="Changing Authentication" />;
    }
  }

  // Description: Render the correct form in the modal
  function ModalForms() {
    switch (modal_step) {
      case ChangeAuthenticationModalSteps.SUBMIT_PHONE_NUMBER:
        return <PhoneNumberForm handleAction={handlePageActions} title="Switch to SMS Verification"></PhoneNumberForm>;
      case ChangeAuthenticationModalSteps.SUBMIT_TOTP_CODE:
        return <>{!!current_account && !!totp_secret_ref.current ? <TOTPForm handleAction={handlePageActions} totp_secret={totp_secret_ref.current} user_id={current_account?.user_id}></TOTPForm> : <></>}</>;
      case ChangeAuthenticationModalSteps.SUBMIT_SMS_CODE:
        return <SharedSmsCodeVerificationForm expires_param={expires} handleAction={handlePageActions} title={sms_verification_title_ref.current} />;
      case ChangeAuthenticationModalSteps.SUBMIT_TOTP_CURRENT_CODE:
        return <CurrentTOTPCodeVerificationForm handleAction={handlePageActions} />;
      case ChangeAuthenticationModalSteps.WEB_AUTHN_KEY_ADDED:
        return <WebAuthKeyAdded handleAction={handlePageActions} new_webkey={auth_type === AuthenticationTypes.WEBAUTHN_CREDENTIAL && new_auth_type_ref.current === AuthenticationTypes.WEBAUTHN_CREDENTIAL } />;
      default:
        return <Loading className="position-absolute" />;
    }
  }

  return <>
    <PageHeader>
      <h1>Change Authentication</h1>
    </PageHeader>
    <Card className="card-section">
      <ChangeAuthenticationForms />
      <Modal className="change-auth-modal" show={modal_show} onHide={() => ChangeAuthenticationRequests.handleResetForms()} centered aria-labelledby="change authentication modal">
        <Modal.Body>
          <ModalForms />
        </Modal.Body>
      </Modal>
    </Card>
  </>;
}

export default React.memo(ChangeAuthenticationComponent);
